import { useEffect, useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import EvalVerdictDetails, { type EvalVerdict } from '@/frontend/components/Eval/EvalVerdictDetails'
import EvalDateFilter, { windowLabel, windowParams } from '@/frontend/components/Eval/EvalDateFilter'
import EvalHelp from '@/frontend/components/Eval/EvalHelp'
import { RES_PT, SAT_PT, RES_BAR, SAT_BAR, RES_ORDER, SAT_ORDER, RES_CHIP, SAT_CHIP, SEV_DOT, SEV_PT, severityKey } from '@/frontend/components/Eval/evalStyles'

interface LedgerRow extends EvalVerdict {
  id: string
  baseSessionId: string
  caseGroupId?: string
  conductorRunId?: string | null
  conductorCaseId?: string | null
  rootSessionKey?: string | null
  hasTelemetrySession: boolean
}

interface SyncRequest {
  id: string
  status: 'pending' | 'running' | 'done' | 'failed'
  requestedAt: string
  completedAt: string | null
  stats: { sessions_judged?: number; error?: string }
}

// "Sincronizar agora" button + last request status. Polls while the request is
// in flight and does a FULL reload when it finishes, so totals/charts/ledger
// reflect the freshly judged sessions.
function SyncControl({ workerId, sync }: { workerId: string; sync: SyncRequest | null }) {
  const inFlight = sync?.status === 'pending' || sync?.status === 'running'
  const prevStatus = useRef(sync?.status)
  useEffect(() => {
    const was = prevStatus.current
    prevStatus.current = sync?.status
    if ((was === 'pending' || was === 'running') && sync?.status && !['pending', 'running'].includes(sync.status)) {
      router.reload() // sync finished: refresh verdicts, not just the chip
    }
  }, [sync?.status])
  useEffect(() => {
    if (!inFlight) return
    const t = setInterval(() => router.reload({ only: ['syncRequest'] }), 8000)
    return () => clearInterval(t)
  }, [inFlight])

  const time = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return (
    <div className="flex items-center gap-2">
      {sync && (
        inFlight ? (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-2 py-1 text-xs text-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
            <Loader2 className="h-3 w-3 animate-spin" />
            {sync.status === 'running' ? 'worker julgando agora…' : `na fila (pedido ${time(sync.requestedAt)})`}
          </span>
        ) : sync.status === 'done' ? (
          <span className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
            sync {sync.completedAt ? time(sync.completedAt) : ''}: {sync.stats?.sessions_judged ?? 0} julgadas
          </span>
        ) : (
          <span className="rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-900 dark:bg-red-950/30 dark:text-red-200"
                title={sync.stats?.error}>
            sync falhou{sync.completedAt ? ` (${time(sync.completedAt)})` : ''}
          </span>
        )
      )}
      <button
        onClick={() => router.post(`/cc/eval/${workerId}/sync`, {}, { preserveScroll: true })}
        disabled={inFlight}
        className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
        title="Pede ao worker da máquina para julgar JÁ as sessões mais recentes desta instância"
      >
        <RefreshCw className={`h-4 w-4 ${inFlight ? 'animate-spin' : ''}`} /> Sincronizar agora
      </button>
    </div>
  )
}

interface Props {
  windowDays: number | null
  windowFrom: string | null
  windowTo: string | null
  judgeVersion: string
  configuredJudgeVersion: string | null
  configuredCount: number
  judgeVersions: Array<{ version: string; count: number }>
  digitalWorker: { id: string; name: string; instanceName: string; organization: string | null; machine: string | null }
  totals: { judged: number; problems: number; grave: number; masked: number; neededHuman: number; neededKnown: number; corrections: number }
  prevTotals: { judged: number; problems: number; grave: number; masked: number; neededHuman: number; neededKnown: number; corrections: number }
  resolutionHist: Record<string, number>
  satisfactionHist: Record<string, number>
  byDay: Array<{ day: string; judged: number; grave: number; leve: number }>
  byModel: Array<{ model: string; judged: number; problems: number; resolved: number; corrections: number }>
  ledger: { filter: string; page: number; total: number; perPage: number; rows: LedgerRow[] }
  syncRequest: SyncRequest | null
}

// Fixed semantic order + colors: good on top, bad in red — comparable at a glance.
function Hist({ title, hist, pt, bar, order }: {
  title: string
  hist: Record<string, number>
  pt: Record<string, string>
  bar: Record<string, string>
  order: string[]
}) {
  const clean = Object.fromEntries(Object.entries(hist).filter(([k]) => k && k !== 'null'))
  const total = Object.values(clean).reduce((a, b) => a + b, 0) || 1
  const keys = [...order.filter((k) => clean[k]), ...Object.keys(clean).filter((k) => !order.includes(k))]
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="space-y-1.5">
        {keys.map((k) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <span className={`h-2 w-2 shrink-0 rounded-full ${bar[k] ?? 'bg-primary/70'}`} />
            <span className="w-24 shrink-0">{pt[k] ?? k}</span>
            <div className="h-3 flex-1 overflow-hidden rounded bg-muted">
              <div className={`h-full ${bar[k] ?? 'bg-primary/70'}`} style={{ width: `${(100 * clean[k]) / total}%` }} />
            </div>
            <span className="w-24 shrink-0 whitespace-nowrap text-right tabular-nums text-muted-foreground">{clean[k]} · {Math.round((100 * clean[k]) / total)}%</span>
          </div>
        ))}
        {keys.length === 0 && <span className="text-xs text-muted-foreground">sem dados</span>}
      </div>
    </div>
  )
}

// Period-over-period delta chip vs the previous window of equal length. Green =
// improved, red = worsened (every metric here is "lower is better"); volume is
// neutral. 'rate' compares percentages (pp); 'count'/'volume' compare absolutes.
function Trend({ cur, prev, judged, prevJudged, kind = 'rate' }: {
  cur: number; prev: number; judged?: number; prevJudged?: number; kind?: 'rate' | 'count' | 'volume'
}) {
  if (kind === 'rate') {
    if (!judged) return <span className="text-[10px] text-muted-foreground">sem dados na janela</span>
    if (!prevJudged) return <span className="text-[10px] text-muted-foreground">sem período anterior</span>
    const d = (100 * cur) / judged - (100 * prev) / prevJudged
    if (Math.abs(d) < 0.5) return <span className="text-[10px] text-muted-foreground">≈ estável vs anterior</span>
    return <span className={`text-[10px] font-medium ${d > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{d > 0 ? '▲' : '▼'} {Math.abs(d).toFixed(0)}pp vs anterior</span>
  }
  // count / volume — suppress when either window is empty so a new or idle
  // worker doesn't get a misleading absolute delta against no sample.
  if (!judged) return <span className="text-[10px] text-muted-foreground">sem dados na janela</span>
  if (!prevJudged) return <span className="text-[10px] text-muted-foreground">sem período anterior</span>
  const d = cur - prev
  if (d === 0) return <span className="text-[10px] text-muted-foreground">= estável vs anterior</span>
  const color = kind === 'volume' ? 'text-muted-foreground' : (d > 0 ? 'text-red-600' : 'text-emerald-600')
  return <span className={`text-[10px] font-medium ${color}`}>{d > 0 ? '▲' : '▼'} {Math.abs(d)} vs anterior</span>
}

export default function Show({ windowDays, windowFrom, windowTo, judgeVersion, configuredJudgeVersion, configuredCount, judgeVersions, digitalWorker, totals, prevTotals, resolutionHist, satisfactionHist, byDay, byModel, ledger, syncRequest }: Props) {
  const pct = (a: number, b: number) => (b > 0 ? `${Math.round((100 * a) / b)}%` : '–')
  const maxDay = Math.max(...byDay.map((d) => d.judged), 1)
  const win = { days: windowDays, from: windowFrom, to: windowTo }
  const setFilter = (filter: string) =>
    router.get(`/cc/eval/${digitalWorker.id}`, { filter, judge_version: judgeVersion, ...windowParams(win) }, { preserveState: true, preserveScroll: true })
  const setPage = (page: number) =>
    router.get(`/cc/eval/${digitalWorker.id}`, { filter: ledger.filter, page, judge_version: judgeVersion, ...windowParams(win) }, { preserveState: true, preserveScroll: true })
  const totalPages = Math.max(1, Math.ceil(ledger.total / ledger.perPage))

  return (
    <div className="h-full overflow-y-auto">
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link href="/cc/eval" className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline">
            <ArrowLeft className="h-3 w-3" /> eval da frota
          </Link>
          <h1 className="text-xl font-semibold">{digitalWorker.instanceName}</h1>
          <p className="text-sm text-muted-foreground">
            {digitalWorker.organization} · {digitalWorker.machine} · {windowLabel(win)} · juiz {judgeVersion}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SyncControl workerId={digitalWorker.id} sync={syncRequest} />
          <select
            className="rounded-md border bg-background px-2 py-1.5 text-sm"
            value={judgeVersion}
            title="Série de julgamento (judge_version)"
            onChange={(e) => router.get(`/cc/eval/${digitalWorker.id}`, { judge_version: e.target.value, ...windowParams(win) }, { preserveState: true })}
          >
            {judgeVersions.map((v) => (
              <option key={v.version} value={v.version}>{v.version} ({v.count})</option>
            ))}
          </select>
          <EvalDateFilter window={win} onChange={(params) => router.get(`/cc/eval/${digitalWorker.id}`, { judge_version: judgeVersion, ...params }, { preserveState: true })} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {[
          { v: `${totals.judged}`, l: 'sessões de cliente julgadas',
            d: <Trend cur={totals.judged} prev={prevTotals.judged} judged={totals.judged} prevJudged={prevTotals.judged} kind="volume" />,
            h: 'Sessões user/qa julgadas na janela. System/boot são amostradas e ficam no filtro "Sistema".' },
          { v: `${totals.problems} (${pct(totals.problems, totals.judged)})`, l: 'com problema',
            d: <Trend cur={totals.problems} prev={prevTotals.problems} judged={totals.judged} prevJudged={prevTotals.judged} />,
            h: 'Não resolvida/abandonada, cliente frustrado/irritado, severidade ≥ major, sucesso falso ou erro hard de tool. Inclui problemas leves — veja "graves" para o subconjunto sério.' },
          { v: `${totals.grave} (${pct(totals.grave, totals.judged)})`, l: 'graves (sev. ≥ major)',
            d: <Trend cur={totals.grave} prev={prevTotals.grave} judged={totals.judged} prevJudged={prevTotals.judged} />,
            h: 'Sessões com severidade major/crítico — o subconjunto realmente sério dos problemas (o que merece atenção primeiro).' },
          { v: `${totals.masked} (${pct(totals.masked, totals.judged)})`, l: 'sucesso falso',
            d: <Trend cur={totals.masked} prev={prevTotals.masked} judged={totals.judged} prevJudged={prevTotals.judged} />,
            h: 'Agente declarou sucesso ao cliente, mas os outputs das tools mostram falha. Coexiste com "Resolvida": o desfecho é a versão do cliente; este é a auditoria técnica desmentindo.' },
          { v: `${totals.neededHuman} (${pct(totals.neededHuman, totals.neededKnown)})`, l: 'precisou de humano',
            d: <Trend cur={totals.neededHuman} prev={prevTotals.neededHuman} judged={totals.neededKnown} prevJudged={prevTotals.neededKnown} />,
            h: 'A tarefa só terminou (ou terminaria) com intervenção humana — pergunta-base do sz-eval. % sobre sessões em que a pergunta foi respondida (vereditos legados não têm).' },
          { v: `${totals.corrections}`, l: 'correções do cliente',
            d: <Trend cur={totals.corrections} prev={prevTotals.corrections} judged={totals.judged} prevJudged={prevTotals.judged} kind="count" />,
            h: 'Vezes que o cliente corrigiu o agente na conversa ("não era isso", dado errado, refazer).' },
        ].map((k, i) => (
          <div key={i} className="cursor-help rounded-lg border bg-card p-3" title={k.h}>
            <div className="text-xl font-semibold tabular-nums">{k.v}</div>
            <div className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">{k.l}</div>
            <div className="mt-1 leading-tight">{k.d}</div>
          </div>
        ))}
      </div>
      <p className="-mt-1 text-[10px] text-muted-foreground">
        Setas = variação vs o período anterior de mesma duração (verde = melhorou · vermelho = piorou).
      </p>

      {configuredJudgeVersion && configuredJudgeVersion !== judgeVersion && (
        <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
          A série do juiz novo (<code>{configuredJudgeVersion}</code>) está em repopulação — <b>{configuredCount}</b> vereditos
          até agora. Mostrando <code>{judgeVersion}</code> enquanto isso.
        </div>
      )}

      <EvalHelp />

      <section className="rounded-lg border bg-card p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Linha do tempo (barras = julgadas)
          </span>
          <span className="flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> grave/crítico</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" /> problema leve</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-zinc-300 dark:bg-zinc-600" /> ok</span>
          </span>
        </div>
        <div className="flex h-24 items-end gap-1 overflow-x-auto">
          {byDay.map((d) => (
            <div key={d.day} className="flex min-w-5 flex-1 flex-col items-center gap-0.5"
                 title={`${d.day}: ${d.judged} julgadas · ${d.grave} grave/crítico · ${d.leve} problema leve · ${d.judged - d.grave - d.leve} ok`}>
              <div className="flex w-full max-w-8 flex-col justify-end" style={{ height: '72px' }}>
                <div className="w-full rounded-t bg-red-500" style={{ height: `${(d.grave / maxDay) * 100}%` }} />
                <div className="w-full bg-amber-400" style={{ height: `${(d.leve / maxDay) * 100}%` }} />
                <div className="w-full bg-zinc-300 dark:bg-zinc-600" style={{ height: `${((d.judged - d.grave - d.leve) / maxDay) * 100}%` }} />
              </div>
              <span className="text-[9px] text-muted-foreground">{d.day.slice(8)}/{d.day.slice(5, 7)}</span>
            </div>
          ))}
          {byDay.length === 0 && <span className="text-sm text-muted-foreground">sem dados na janela</span>}
        </div>
      </section>

      <div className="grid gap-3 md:grid-cols-3">
        <Hist title="Desfecho do pedido" hist={resolutionHist} pt={RES_PT} bar={RES_BAR} order={RES_ORDER} />
        <Hist title="Satisfação (sinal explícito)" hist={satisfactionHist} pt={SAT_PT} bar={SAT_BAR} order={SAT_ORDER} />
        <div className="rounded-lg border bg-card p-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Por modelo</div>
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase text-muted-foreground">
              <tr><th>modelo</th><th className="text-right">n</th><th className="text-right">prob</th><th className="text-right">resolv</th><th className="text-right">corr</th></tr>
            </thead>
            <tbody>
              {byModel.map((m) => (
                <tr key={m.model} className="border-t">
                  <td className="py-1 font-medium">{m.model}</td>
                  <td className="py-1 text-right tabular-nums">{m.judged}</td>
                  <td className="py-1 text-right tabular-nums">{m.problems}</td>
                  <td className="py-1 text-right tabular-nums">{m.resolved}</td>
                  <td className="py-1 text-right tabular-nums">{m.corrections}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Diário de sessões ({ledger.total})
          </h2>
          <div className="flex gap-1">
            {[
              ['all', 'Cliente'], ['problems', 'Com problema'], ['grave', 'Graves'], ['system', 'Sistema'],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`rounded-md border px-2.5 py-1 text-xs ${ledger.filter === key ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Severity legend — the leading dot is its OWN axis (worst of outcome +
            execution quality), independent of whether the request was resolved. */}
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
          <span className="font-medium uppercase tracking-wide">severidade</span>
          {(['none', 'minor', 'major', 'critical'] as const).map((sev) => (
            <span key={sev} className="flex items-center gap-1">
              <span className={`h-2 w-2 rounded-full ${SEV_DOT[sev]}`} />
              {SEV_PT[sev]}
            </span>
          ))}
          <span className="text-muted-foreground/70">— o quão sério foi o pior problema da conversa (independe de resolver)</span>
        </div>

        <div className="space-y-1.5">
          {ledger.rows.map((row) => (
            <details key={row.id} className="rounded-lg border bg-card">
              <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-3 py-2 text-sm">
                {(() => { const sk = severityKey(row.severity, row.problem); return (
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${SEV_DOT[sk] ?? 'bg-zinc-400'}`} title={`severidade: ${SEV_PT[sk]} — o quão sério foi o pior problema que rolou na conversa`} />
                ) })()}
                <span className="font-mono text-xs text-muted-foreground">
                  {row.startedAt ? new Date(row.startedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                </span>
                {row.sessionId.includes('#') && (
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                        title="Atendimento dentro de uma sessão longa (segmentado por intervalo de tempo)">
                    atend. {Number(row.sessionId.split('#')[1]) + 1}
                  </span>
                )}
                <span className="flex-1 truncate">{row.lenses?.outcome?.task_summary ?? '(sem resumo)'}</span>
                {row.resolution && row.resolution !== 'no_task' && (
                  <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase ${RES_CHIP[row.resolution] ?? ''}`}
                        title="Desfecho na visão do CLIENTE (lente de desfecho lê só o diálogo)">{RES_PT[row.resolution] ?? row.resolution}</span>
                )}
                {row.satisfaction && SAT_CHIP[row.satisfaction] && row.satisfaction !== 'neutral' && (
                  <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase ${SAT_CHIP[row.satisfaction]}`}
                        title="Sinal explícito de satisfação do cliente na conversa">{SAT_PT[row.satisfaction]}</span>
                )}
                {row.masked && (
                  <span className="cursor-help rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white"
                        title="Agente declarou sucesso, mas os outputs das tools mostram falha — por isso pode aparecer junto de 'Resolvida' (a versão do cliente). Evidência na seção Execução.">sucesso falso</span>
                )}
                {row.corrections > 0 && (
                  <span className="rounded border px-1.5 py-0.5 text-[10px]"
                        title="Correções do cliente na conversa">{row.corrections} corr.</span>
                )}
                {row.kind === 'qa' && <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] uppercase text-white">QA</span>}
              </summary>
              <div className="border-t px-3 py-2">
                <EvalVerdictDetails verdict={row} />
                {row.hasTelemetrySession && (
                  <Link
                    href={`/cc/telemetry/digital_workers/${digitalWorker.id}/sessions/${encodeURIComponent(row.baseSessionId)}${row.sessionId.includes('#') ? `?atend=${row.sessionId.split('#')[1]}` : ''}`}
                    className="mt-2 inline-block text-xs text-primary hover:underline"
                  >
                    {row.sessionId.includes('#') ? 'abrir este atendimento (telemetria) →' : 'abrir sessão completa (telemetria) →'}
                  </Link>
                )}
              </div>
            </details>
          ))}
          {ledger.rows.length === 0 && (
            <p className="rounded-lg border px-3 py-6 text-center text-sm text-muted-foreground">Nenhuma sessão neste filtro.</p>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-3 flex items-center justify-center gap-3 text-sm">
            <button disabled={ledger.page <= 1} onClick={() => setPage(ledger.page - 1)} className="rounded border p-1 disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-muted-foreground">página {ledger.page} de {totalPages}</span>
            <button disabled={ledger.page >= totalPages} onClick={() => setPage(ledger.page + 1)} className="rounded border p-1 disabled:opacity-40">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </section>
    </div>
    </div>
  )
}

Show.layout = (page: React.ReactElement) => <CommandCenterLayout variant="fullscreen">{page}</CommandCenterLayout>
